import { ReferralsActionTypes } from './types';
import { eventChannel, EventChannel } from 'redux-saga';
import { all, takeLatest, call, put, select, take } from 'redux-saga/effects';
import Services from '../../services';
import { initAppFailure, initAppSuccess } from 'redux/app/actions';
import {
  connectWebsocketForReferral,
  fetchReferralFailure,
  fetchReferralHistoryFailure,
  fetchReferralHistorySuccess,
  fetchReferralSuccess,
  reprocessReferralsFailure,
  reprocessReferralsSuccess,
  updateEnquiryReferral
} from './actions';
import { AppState } from 'redux/app/types';
import Utils from 'utilities/helper.util';
import { Params } from 'redux/members/types';
import { getJobStatusCodeMsg } from 'appConstants/enquiry';
import { WebSocketConnectionOption, AUTH_TYPE } from 'types';

export function* doFetchReferralById(request: any) {
  try {
    const {
      enquiryId,
      referralId,
      returnResponses = true,
      returnGeometry = true
    } = request.payload;

    const resopnse = yield call(
      Services.referral.fetchReferralByIdService,
      enquiryId,
      referralId,
      returnResponses,
      returnGeometry,
      true // logo
    );
    if (!resopnse.error) {
      yield put(fetchReferralSuccess(resopnse));
    } else {
      const errMsg = resopnse.error?.message;
      yield put(fetchReferralFailure(errMsg));
    }
  } catch (error) {
    const errorMsg = error?.message ? error.message : error;
    yield put(fetchReferralFailure(errorMsg));
  }
}

export function* doFetchReferralHistory(request: any) {
  const queryParams: Params = request.payload;
  try {
    const response = yield call(
      Services.referral.fetchAllReferralsService,
      queryParams
    );
    if (!response.errors) {
      const responseBody = response.Referrals.map((item: any) => ({
        id: item.id,
        enquiryId: item.Enquiry.id,
        externalId: item.externalId,
        updatedAt: Utils.toLocalDateTimeFormat(item.updatedAt),
        job: {
          externalId: item.Enquiry.externalId,
          start: Utils.toLocalDateFormat(item.Enquiry.createdAt)
        },
        enquirer: {
          fullname: `${item.Enquiry.User.firstName} ${item.Enquiry.User.lastName}`,
          email: item.Enquiry.User.email
        },
        userRef: item.Enquiry.userReference,
        status: item.status ? getJobStatusCodeMsg(item.status) : 'TO DO',
        address: Utils.getFullAddress(item.Enquiry.Address),
        isReprocess: item.isReprocess
      }));

      const referrals = {
        items: responseBody,
        count: response.Info?.count,
        queryCreatedBefore: response.queryCreatedBefore,
        queryCreatedAfter: response.queryCreatedAfter
      };
      yield put(fetchReferralHistorySuccess(referrals));
      yield put(initAppSuccess());
    } else {
      yield put(fetchReferralHistoryFailure(response.errors[0].message));
    }
  } catch (error) {
    const errorMsg = error.message ? error.message : error;
    yield put(fetchReferralHistoryFailure(errorMsg));
    yield put(initAppFailure());
  }
}

function* doReferralReprocess(request: any) {
  try {
    const response = yield call(
      Services.referral.reprocessReferralsService,
      request.payload
    );

    if (
      response &&
      (response.error || typeof response.totalReprocessed === 'undefined')
    ) {
      return yield put(reprocessReferralsFailure(response.error));
    }

    yield put(reprocessReferralsSuccess());
  } catch (error) {
    const errorMsg = error.message ? error.message : error;
    yield put(reprocessReferralsFailure(errorMsg));
    yield put(initAppFailure());
  }
}

export function* onFetchReferralById() {
  yield takeLatest(
    ReferralsActionTypes.FETCH_REFERRAL_BYID,
    doFetchReferralById
  );
}

export function* onFetchReferralHistory() {
  yield takeLatest(
    ReferralsActionTypes.FETCH_REFERRAL_HISTORY,
    doFetchReferralHistory
  );
}

function* onReferralReprocess() {
  yield takeLatest(
    ReferralsActionTypes.REPROCESS_REFERRALS_START,
    doReferralReprocess
  );
}

export function* enquiriesSagas() {
  yield all([
    call(onFetchReferralHistory),
    call(onFetchReferralById),
    call(onConnectWebsocketForReferral),
    call(onReferralReprocess)
  ]);
}

export function* doConnectWebsocketForReferral(request: any) {
  const { enquiryId, referralId } = request.payload;
  try {
    const getAppState = (state: any) => state.app;
    const appState: AppState = yield select(getAppState);
    const auth: { user?: { username?: string; token?: string } } = yield call(
      Services.auth.fetchCurrentUser,
      appState.loginType || AUTH_TYPE.BASIC_LOGIN
    );
    const token = auth.user?.token || '';
    const entityId = referralId;
    const clientId = Services.auth.getAuthClientId(
      appState.loginType || AUTH_TYPE.BASIC_LOGIN
    );
    const channel: EventChannel<any> = yield call(websocketForReferralChannel, {
      enquiryId,
      entityId,
      clientId,
      token
    });

    while (true) {
      const action: { type: string; payload: any } = yield take(channel);
      yield put(action);
    }
  } catch (error) {
    console.error(error);
  }
}

export function* onConnectWebsocketForReferral() {
  yield takeLatest(
    ReferralsActionTypes.CONNECT_WEBSOCKET_FOR_REFERRAL,
    doConnectWebsocketForReferral
  );
}

function websocketForReferralChannel({
  enquiryId,
  entityId,
  clientId,
  token
}: {
  enquiryId: number;
  entityId: number;
  clientId: string;
  token: string;
}) {
  return eventChannel((emitter) => {
    const onClose = () => {
      return emitter(
        connectWebsocketForReferral({
          enquiryId,
          referralId: entityId
        })
      );
    };

    const onMessage = async (event: MessageEvent) => {
      try {
        const body = JSON.parse(event.data);
        const { referralId, status } = body;
        if (status === 'COMPLETED') {
          const referral = await Services.referral.fetchReferralByIdService(
            enquiryId,
            referralId,
            true,
            false,
            true
          );
          return emitter(updateEnquiryReferral(referral));
        }
      } catch (err) {
        console.error(err);
      }
    };

    const option: WebSocketConnectionOption = {
      entity: 'REFERRAL',
      entityId,
      token,
      clientId,
      onMessage,
      onClose
    };
    Services.websocket.createWebSocketConnection(option);
    // unsubscribe function
    return () => {
      // have to put something for es-lint
      console.log('websocket unsubscribe');
    };
  });
}

export default enquiriesSagas;
